import { Pipe, PipeTransform } from '@angular/core'
import { ZonedDateTime, ZoneId } from '@js-joda/core'
import { DateFormat, JodaConvertible, jodaFormatPatternWithLocale, stringToZonedDateTime } from '../../util'

export interface DateFormatOptions {
  /**
   * The format to format the date in
   */
  format: DateFormat

  /**
   * Default value to return if given value is falsy or can't be converted (defaults to empty string)
   */
  defaultValue?: string

  /**
   * The timezone to use to convert the value to for display.
   * Only applies to ZonedDateTime values or strings parsed as ZonedDateTime.
   */
  timeZoneId?: ZoneId | string
}

@Pipe({ standalone: true, name: 'ftrDate' })
/**
 * Converts the given date to a human readable format.
 *
 * @see DateComponent
 */
export class DatePipe implements PipeTransform {
  transform(value: JodaConvertible | null | undefined, options: DateFormatOptions): string {
    const { defaultValue = '' } = options
    const timeZoneId = options.timeZoneId ? ZoneId.of(options.timeZoneId.toString()) : undefined

    if (value === undefined || value === null || value === '') {
      return defaultValue
    }

    try {
      if (value instanceof Date) {
        value = stringToZonedDateTime(value.toISOString(), timeZoneId ?? ZoneId.systemDefault())
      }

      if (value instanceof ZonedDateTime && timeZoneId) {
        value = value.withZoneSameInstant(timeZoneId)
      }

      if (typeof value === 'string') {
        value = stringToZonedDateTime(value, timeZoneId ?? ZoneId.systemDefault())
      }

      return value?.format(jodaFormatPatternWithLocale(options.format)) ?? defaultValue
    } catch {
      return defaultValue
    }
  }
}
